import { Form, type FormInstance } from "../ui";
import { useCallback, useRef, useState } from "react";
import { formSnapshot } from "./formSnapshot";
export { formSnapshot } from "./formSnapshot";

/** Explicit saved baseline, including programmatic changes and reverting an edit. */
export function useFormDirty<T extends object>(form: FormInstance<T>) {
  // Observe an immutable snapshot: Ant's all-values object can be mutated in place.
  const snapshot = Form.useWatch(formSnapshot, { form, preserve: true });
  const [baseline, setBaseline] = useState<string | null>(null);
  const baselineRef = useRef<string | null>(null);
  const markSaved = useCallback((savedValues: unknown) => {
    const snapshot = formSnapshot(savedValues);
    baselineRef.current = snapshot;
    setBaseline(snapshot);
  }, []);
  const resetDirty = useCallback(() => {
    markSaved(form.getFieldsValue(true));
  }, [form, markSaved]);
  const initialize = useCallback((initialValues: Parameters<FormInstance<T>["setFieldsValue"]>[0]) => {
    form.setFieldsValue(initialValues);
    resetDirty();
  }, [form, resetDirty]);
  const sync = useCallback((initialValues: Parameters<FormInstance<T>["setFieldsValue"]>[0]) => {
    // A background reload must not replace a draft. Explicit opening uses initialize.
    if (baselineRef.current !== null && formSnapshot(form.getFieldsValue(true)) !== baselineRef.current) return;
    initialize(initialValues);
  }, [form, initialize]);
  return { ready: baseline !== null, dirty: baseline !== null && snapshot !== baseline, initialize, sync, resetDirty, markSaved };
}

